import { PublicationError } from '@streamlain/doc-publication-contract';
import {
  localAccountKeyError,
  type LocalDataKind,
  LocalDataSealer,
} from '@streamlain/view-config/local-data-seal';
import type { IDBPTransaction, StoreNames } from 'idb';

import {
  type LocalDataKeyOperation,
  LocalDataKeyProvider,
} from '../../local-data/key-provider';
import {
  containerScope,
  type LocalDataContainer,
  type LocalDocKeyRecord,
} from '../../local-data/payload';
import { isPlainLocalData } from '../../local-data/seal';
import type { IDBConnection, IDBConnectionOptions } from './db';
import { withIdbRetry } from './retry';
import type { DocStorageSchema } from './schema';

type Tx<M extends IDBTransactionMode> = IDBPTransaction<
  DocStorageSchema,
  StoreNames<DocStorageSchema>[],
  M
>;
export const localCipherReferenceStores = [
  'updates',
  'snapshots',
  'blobs',
  'blobData',
  'publications',
  'indexSegments',
] as const;
class KeyCASConflict extends Error {
  constructor() {
    super('KEY_CAS_CONFLICT');
  }
}

function publicationScope(stores: readonly StoreNames<DocStorageSchema>[]) {
  return stores.some(name =>
    [
      'authState',
      'publications',
      'publicationActors',
      'publicationMeta',
      'remotePublicationMarks',
    ].includes(name)
  );
}

export class IDBEncryptionContext {
  operation: LocalDataKeyOperation | null = null;
  readonly registries = new Map<string, LocalDocKeyRecord | null>();
  readonly candidates = new Set<string>();
  readonly keys = new Map<string, { keyId: string; key: CryptoKey }>();
  constructor(
    readonly owner: IDBEncryption,
    readonly publication = false
  ) {}
  async capture(tx: Tx<'readonly'>, scopeId: string, writing: boolean) {
    if (this.owner.plain || this.registries.has(scopeId)) return;
    const record =
      (await tx
        .objectStore('docKeyRegistry')
        .index('keyScopeId')
        .get(scopeId)) ?? null;
    if (!record && writing) {
      for (const name of localCipherReferenceStores)
        if (await tx.objectStore(name).index('keyScopeId').count(scopeId))
          throw localAccountKeyError('KEY_UNAVAILABLE');
    }
    this.registries.set(scopeId, record);
  }
  async prepare(writing: boolean) {
    if (this.owner.plain) return;
    const provider = this.owner.provider!;
    const operation = (this.operation = await provider.beginOperation());
    await Promise.all(
      [...this.registries].map(async ([id, stored]) => {
        if (!stored && !writing) return;
        const record = stored ?? (await provider.candidate(id, operation));
        if (!stored) {
          this.registries.set(id, record);
          this.candidates.add(id);
        }
        this.keys.set(
          id,
          await provider.getKey(record, id, !!stored, operation)
        );
      })
    );
  }

  async seal(
    keyScopeId: string,
    docId: string,
    kind: LocalDataKind,
    plain: Uint8Array
  ): Promise<LocalDataContainer> {
    const revision = crypto.randomUUID();
    const scope = { ...this.owner.identity(), docId, kind, revision };
    const sealed = await new LocalDataSealer({
      getKey: async () => this.key(keyScopeId),
    }).seal(scope, plain);
    return { keyScopeId, revision, sealed };
  }
  async open(
    keyScopeId: string,
    docId: string,
    kind: LocalDataKind,
    payload: LocalDataContainer | undefined
  ) {
    if (!payload || payload.keyScopeId !== keyScopeId)
      throw localAccountKeyError('CORRUPT');
    return new LocalDataSealer({
      getKey: async () => this.key(keyScopeId),
    }).open(
      containerScope({ ...this.owner.identity(), docId, kind }, payload),
      payload.sealed
    );
  }
  /** Retain only the authority token after a completed index batch/rebuild. */
  releasePreparedKeys() {
    this.keys.clear();
    this.registries.clear();
    this.candidates.clear();
    // beginOperation owns this snapshot Map, independently of the provider LRU.
    const cached = this.operation?.cached;
    if (cached instanceof Map) cached.clear();
  }

  private key(id: string) {
    const key = this.keys.get(id);
    if (!key) throw localAccountKeyError('KEY_UNAVAILABLE');
    return key;
  }
}

/** Read/copy -> crypto -> commit. No custody, WebCrypto or Yjs awaits inside IDB transactions. */
export class IDBEncryption {
  readonly provider: LocalDataKeyProvider | null;
  readonly plain: boolean;
  constructor(
    readonly options: IDBConnectionOptions,
    private readonly connection: IDBConnection
  ) {
    const binding = options.authBinding;
    // Keep construction usable for identity comparisons; every operation validates before its first request.
    this.plain =
      binding !== undefined &&
      ((options.flavour === 'local' && binding === null) ||
        (options.flavour !== 'local' &&
          !!binding &&
          binding.accountId.startsWith('external:'))) &&
      isPlainLocalData({ accountKey: binding?.accountId ?? 'local' }, binding);
    this.provider =
      binding && !this.plain ? new LocalDataKeyProvider(binding) : null;
  }
  validate() {
    const binding = this.options.authBinding;
    if (
      binding === undefined ||
      (binding === null && this.options.flavour !== 'local') ||
      (binding !== null && this.options.flavour === 'local') ||
      (binding &&
        (!binding.accountId ||
          !binding.workspaceId ||
          !Number.isSafeInteger(binding.authEpoch) ||
          binding.authEpoch < 1))
    )
      throw new PublicationError('LOCKED');
  }
  identity() {
    this.validate();
    const binding = this.options.authBinding;
    if (!binding) throw localAccountKeyError('LOCKED');
    return { accountId: binding.accountId, workspaceId: binding.workspaceId };
  }
  async assertAuth<M extends IDBTransactionMode>(tx: Tx<M>) {
    this.validate();
    const binding = this.options.authBinding;
    if (binding) {
      const state = await tx.objectStore('authState').get(binding.accountId);
      if (
        !state ||
        state.authEpoch !== binding.authEpoch ||
        state.logoutPending
      ) {
        this.provider?.clear();
        throw new PublicationError('LOCKED');
      }
    }
  }
  async transaction<M extends IDBTransactionMode, T>(
    stores: StoreNames<DocStorageSchema>[],
    mode: M,
    work: (tx: Tx<M>) => Promise<T>,
    signal?: AbortSignal
  ): Promise<T> {
    return withIdbRetry(this.connection, () =>
      this.transactionOnce(stores, mode, work, signal, true)
    );
  }
  /** Keyed read/commit steps: their operation checks authority around crypto. */
  private operationTransaction<M extends IDBTransactionMode, T>(
    stores: StoreNames<DocStorageSchema>[],
    mode: M,
    work: (tx: Tx<M>) => Promise<T>,
    signal?: AbortSignal
  ): Promise<T> {
    return withIdbRetry(this.connection, () =>
      this.transactionOnce(stores, mode, work, signal, false)
    );
  }
  private async transactionOnce<M extends IDBTransactionMode, T>(
    stores: StoreNames<DocStorageSchema>[],
    mode: M,
    work: (tx: Tx<M>) => Promise<T>,
    signal: AbortSignal | undefined,
    authority: boolean
  ): Promise<T> {
    this.validate();
    if (signal?.aborted) throw new PublicationError('CANCELLED');
    if (authority) this.provider?.assertAuthority();
    // With a key provider the lock lives in memory (LocalDataKeyProvider);
    // only plain data without one reads the authState mirror.
    const checkAuth = !this.provider && publicationScope(stores);
    const tx = this.connection.inner.db.transaction(
      [...new Set([...(checkAuth ? ['authState' as const] : []), ...stores])],
      mode
    );
    const done = tx.done;
    void done.catch(() => {});
    const cancel = () => {
      try {
        tx.abort();
      } catch {
        /* Already completed. */
      }
    };
    signal?.addEventListener('abort', cancel, { once: true });
    try {
      if (checkAuth) await this.assertAuth(tx);
      if (signal?.aborted) throw new PublicationError('CANCELLED');
      const result = await work(tx);
      if (signal?.aborted) throw new PublicationError('CANCELLED');
      // A lock that arrived during the work aborts the commit and the output,
      // for keyed reads and commits too: their check before ran ahead of it.
      this.provider?.assertAuthority();
      if (mode === 'readwrite') tx.commit();
      await done;
      return result;
    } catch (error) {
      cancel();
      await done.catch(() => {});
      if (signal?.aborted) throw new PublicationError('CANCELLED');
      throw error;
    } finally {
      signal?.removeEventListener('abort', cancel);
    }
  }
  /** Whether output may still leave this connection; plain data has no lock. */
  get authorized() {
    return this.provider?.authorized ?? true;
  }
  async read<T>(
    stores: StoreNames<DocStorageSchema>[],
    writing: boolean,
    work: (tx: Tx<'readonly'>, context: IDBEncryptionContext) => Promise<T>,
    signal?: AbortSignal
  ) {
    const context = new IDBEncryptionContext(this, publicationScope(stores));
    const value = await this.operationTransaction(
      this.plain
        ? stores
        : [
            ...stores,
            'docKeyRegistry',
            ...(writing ? localCipherReferenceStores : []),
          ],
      'readonly',
      tx => work(tx, context),
      signal
    );
    await context.prepare(writing);
    return { value, context };
  }
  /** After crypto, before output: the lock in memory, or the mirror for plain publications. */
  async assertRead(context: IDBEncryptionContext) {
    if (!this.plain) {
      await this.provider!.assertOperation(context.operation);
      return;
    }
    if (context.publication)
      await this.operationTransaction(
        ['authState'],
        'readonly',
        async () => {}
      );
  }
  /** assertRead together with a read of `stores` in the same transaction. */
  async assertReadWith<T>(
    context: IDBEncryptionContext,
    stores: StoreNames<DocStorageSchema>[],
    work: (tx: Tx<'readonly'>) => Promise<T>
  ): Promise<T> {
    if (!this.plain) await this.provider!.assertOperation(context.operation);
    return this.operationTransaction(stores, 'readonly', work);
  }
  async commit<T>(
    context: IDBEncryptionContext,
    stores: StoreNames<DocStorageSchema>[],
    work: (tx: Tx<'readwrite'>) => Promise<T>,
    signal?: AbortSignal,
    beforeKeys?: (tx: Tx<'readwrite'>) => Promise<{ value: T } | null>
  ): Promise<T> {
    if (!this.plain) await this.provider!.assertOperation(context.operation);
    const result = await this.operationTransaction(
      this.plain || !context.registries.size
        ? stores
        : [...stores, 'docKeyRegistry'],
      'readwrite',
      async tx => {
        const early = await beforeKeys?.(tx);
        if (early) return { value: early.value, keysCommitted: false };
        for (const [id, prepared] of context.registries) {
          const current =
            (await tx
              .objectStore('docKeyRegistry')
              .index('keyScopeId')
              .get(id)) ?? null;
          if (context.candidates.has(id)) {
            if (current) throw new KeyCASConflict();
            if (!prepared) throw localAccountKeyError('CORRUPT');
            await tx.objectStore('docKeyRegistry').add(prepared);
          } else if (
            !current ||
            !prepared ||
            current.keyId !== prepared.keyId ||
            current.registryRevision !== prepared.registryRevision ||
            current.wrappedDEK.length !== prepared.wrappedDEK.length ||
            current.wrappedDEK.some(
              (byte, index) => byte !== prepared.wrappedDEK[index]
            )
          )
            throw new PublicationError('LOCAL_CONFLICT');
        }
        return { value: await work(tx), keysCommitted: true };
      },
      signal
    );
    for (const id of result.keysCommitted ? context.candidates : []) {
      const record = context.registries.get(id);
      const key = context.keys.get(id);
      if (record && key) this.provider!.remember(record, key.key);
    }
    return result.value;
  }
  async write<T>(work: () => Promise<T>): Promise<T> {
    try {
      return await work();
    } catch (error) {
      if (!(error instanceof KeyCASConflict)) throw error;
      try {
        return await work();
      } catch (next) {
        if (next instanceof KeyCASConflict)
          throw new PublicationError('LOCAL_CONFLICT');
        throw next;
      }
    }
  }
  dispose() {
    this.provider?.dispose();
  }
}
